// Speaking invented words with the browser's speech synthesis.
//
// A constructed language has no recordings and no correct pronunciation, so the
// author picks a locale whose voice reads their spelling the way they hear it.
// Everything here degrades to silence rather than throwing: speech synthesis is
// missing or voiceless on plenty of devices, and a lesson must still work.

export interface VoiceOption {
  locale: string
  label: string
  hint: string
}

/**
 * The locales offered as pronunciation styles. Chosen to span clearly different
 * vowel and consonant systems rather than to be a list of popular languages --
 * the point is that "domus" sounds meaningfully different in each.
 */
export const VOICE_OPTIONS: VoiceOption[] = [
  { locale: 'it-IT', label: 'Italian', hint: 'Open vowels, rolled r, everything pronounced' },
  { locale: 'es-ES', label: 'Spanish', hint: 'Crisp and even, five pure vowels' },
  { locale: 'de-DE', label: 'German', hint: 'Hard consonants, strong stress' },
  { locale: 'fr-FR', label: 'French', hint: 'Nasal vowels, soft endings' },
  { locale: 'ja-JP', label: 'Japanese', hint: 'Even syllables, no consonant clusters' },
  { locale: 'ru-RU', label: 'Russian', hint: 'Dark vowels, palatalised consonants' },
  { locale: 'en-GB', label: 'English', hint: 'Read as though it were English' },
]

export function isSpeechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
}

/** Voices load asynchronously in some browsers; this resolves once they exist. */
function voices(): SpeechSynthesisVoice[] {
  if (!isSpeechSupported()) return []
  return window.speechSynthesis.getVoices()
}

function pickVoice(locale: string): SpeechSynthesisVoice | null {
  const available = voices()
  if (available.length === 0) return null

  // Exact locale first, then any voice for the same language, then give up and
  // let the browser choose -- a wrong accent still beats silence.
  return (
    available.find((v) => v.lang.replace('_', '-') === locale) ??
    available.find((v) => v.lang.slice(0, 2) === locale.slice(0, 2)) ??
    null
  )
}

export interface SpeakOptions {
  locale?: string | null
  rate?: number
  onEnd?: () => void
}

/** Speak a word. Returns false when nothing could be said. */
export function speak(text: string, options: SpeakOptions = {}): boolean {
  const { locale, rate = 0.9, onEnd } = options
  if (!isSpeechSupported() || !locale || !text.trim()) {
    onEnd?.()
    return false
  }

  // Cancel anything queued: overlapping utterances make a lesson unusable when
  // someone clicks replay twice.
  window.speechSynthesis.cancel()

  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = locale
  // Slightly under natural pace: these are unfamiliar words with no context to
  // fall back on.
  utterance.rate = rate

  const voice = pickVoice(locale)
  if (voice) utterance.voice = voice

  if (onEnd) {
    utterance.onend = () => onEnd()
    // onend does not fire if the utterance errors or is cancelled, and a
    // sing-along that stalls forever is worse than one that skips a word.
    utterance.onerror = () => onEnd()
  }

  window.speechSynthesis.speak(utterance)
  return true
}

export function stopSpeaking() {
  if (isSpeechSupported()) window.speechSynthesis.cancel()
}

/** Warm up the voice list, which is empty until first queried in some browsers. */
export function primeVoices() {
  if (!isSpeechSupported()) return
  window.speechSynthesis.getVoices()
}
